import React from 'react';

import {
  GetStaticPaths,
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticProps,
} from 'next';

import {
  ProductPageLayout,
  PlayKitPageLayout,
  PlayKitsLandingPageLayout,
  ProductLandingPageLayout,
  Errors,
} from '@/components';
import { getLanguageFromLocale } from '@/utils/translations/localizationHelpers';

import { NotFound } from '@/components/common';

import { findApplicableErrorDetails } from '@/components/common/Errors/utils';

import { TypeTariffSettings } from '@/components/common/types';

import {
  CONTENT_TYPE_PAGE,
  CONTENT_TYPE_PRODUCT_PAGE,
  COURSE_URLS,
  getContentType,
} from '@/core/constants';

import {
  defaultCacheRevalidation,
  shopifyProductField,
} from '@/core/contentfulConfig';

import {
  TypePage,
  TypePlayKitPage,
  TypePlayKitsLandingPage,
  TypeProduct,
  TypeProductFields,
  TypeProductLandingPage,
  TypeProductPage,
  TypeProductPageFields,
} from '@/core/generated-types';

import { StartingKit } from '@/hooks/useStartingKit';
import { useYotpoScript } from '@/hooks/useYotpoScript';

import getStartingProduct from '@/lib/api/orchestration_subflow/api/getStartingProduct';
import { getStartingInstallments } from '@/lib/api/subscriptionManager/apis/subEngine/getStartingInstallments';
import { TypeSubscriptionPlanInstallmentsResponse } from '@/lib/api/subscriptionManager/types';

import {
  getEntriesByPageType,
  getInventoryStatusCopy,
  InventoryStatusCopy,
  ProductVariantCopy,
  ProductPageGeneralCopy,
  getProductVariantsCopy,
  getProductPageGeneralCopy,
  ContentfulPreviewData,
  getEntries,
} from '@/lib/contentful';

import fetchShopifyProducts from '@/lib/middleware/fetchShopifyProducts';
import { ShopifyProduct, getProductById } from '@/lib/shopify';
import { ReviewTotals, getReviewTotals } from '@/lib/yotpo';

import { loadLayout } from '@/utils/loadLayout';
import getPathsFromEntries from '@/utils/pathBuilder';
import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface ProductPageProps {
  error: string;
  page: TypePlayKitPage | TypePlayKitsLandingPage | TypeProductPage;
  shopifyAdditionalProducts: ShopifyProduct[];
  shopifyAlternativeProduct?: ShopifyProduct;
  inventoryStatusCopy: InventoryStatusCopy | null;
  productVariantsCopy: ProductVariantCopy | null;
  productPageGeneralCopy: ProductPageGeneralCopy | null;
  subscriptionPlanInstallments: TypeSubscriptionPlanInstallmentsResponse;
  theThinkerPlayKit: StartingKit;
  v3Translations: TypePage;
  yotpoReviews?: ReviewTotals;
  tariffSettings?: TypeTariffSettings;
}

const ProductPage = ({
  error,
  page,
  shopifyAdditionalProducts,
  shopifyAlternativeProduct,
  inventoryStatusCopy,
  productVariantsCopy,
  productPageGeneralCopy,
  subscriptionPlanInstallments,
  theThinkerPlayKit,
  v3Translations,
  yotpoReviews,
  tariffSettings,
}: ProductPageProps) => {
  useYotpoScript();

  const flags = useFlags();

  // Safe access to featureFlagName - check if it exists on the page fields
  const featureFlagName = (page?.fields as TypeProductPageFields)
    ?.featureFlagName;

  // Client-side feature flag evaluation (this will have user email + device ID context)
  const isPageEnabled = featureFlagName ? flags[featureFlagName] : true;

  const productFields = page?.fields?.product?.fields as any;

  const shopifyProduct = productFields?.shopifyProduct as ShopifyProduct;

  const { errors, heading } = findApplicableErrorDetails([]);

  if (error) {
    return <Errors errors={errors?.concat(error)} heading={heading} />;
  }

  // If feature flag disables the page, show proper 404 component
  if (featureFlagName && isPageEnabled === false) {
    return <NotFound />;
  }

  switch (getContentType(page)) {
    case CONTENT_TYPE_PRODUCT_PAGE.PLAY_KITS_LANDING_PAGE:
      return (
        <PlayKitsLandingPageLayout {...(page as TypePlayKitsLandingPage)} />
      );

    case CONTENT_TYPE_PRODUCT_PAGE.PLAY_KIT_PAGE:
      return (
        <PlayKitPageLayout
          shopifyProduct={shopifyProduct}
          yotpoReviews={yotpoReviews}
          theThinkerPlayKit={theThinkerPlayKit}
          v3Translations={v3Translations}
          tariffSettings={tariffSettings}
          inventoryStatusCopy={inventoryStatusCopy}
          productVariantsCopy={productVariantsCopy}
          {...(page as TypePlayKitPage)}
        />
      );

    case CONTENT_TYPE_PRODUCT_PAGE.PRODUCT_PAGE:
      return (
        <ProductPageLayout
          shopifyProduct={shopifyProduct}
          shopifyAdditionalProducts={shopifyAdditionalProducts}
          shopifyAlternativeProduct={shopifyAlternativeProduct}
          inventoryStatusCopy={inventoryStatusCopy}
          productVariantsCopy={productVariantsCopy}
          productPageGeneralCopy={productPageGeneralCopy}
          subscriptionPlanInstallments={subscriptionPlanInstallments}
          yotpoReviews={yotpoReviews}
          tariffSettings={tariffSettings}
          {...(page as TypeProductPage)}
        />
      );

    case CONTENT_TYPE_PRODUCT_PAGE.PRODUCT_LANDING_PAGE:
      return <ProductLandingPageLayout {...(page as TypeProductLandingPage)} />;

    default:
      console.warn('No matching product page layout');
      return null;
  }
};

export const getStaticPaths: GetStaticPaths =
  async (): Promise<GetStaticPathsResult> => {
    // [slug].tsx file accepts 3 different page types: Product Page, Play Kit Page, Play Kit Landing Page
    // they all share the same /products/slug route, thus we need to retrieve all possible routes for getStaticPaths.
    const pageTypes = CONTENT_TYPE_PRODUCT_PAGE;

    const getEntries = await getEntriesByPageType({ include: 1 }, pageTypes);

    let paths = getPathsFromEntries(getEntries);

    if (
      !process.env.NEXT_PUBLIC_COURSES_ENABLED ||
      process.env.NEXT_PUBLIC_COURSES_ENABLED === 'false'
    ) {
      // temporarily disable these pages https://lovevery.atlassian.net/browse/GROW-8928
      paths = paths.filter(
        (path) => COURSE_URLS.indexOf(path.params.slug || '') === -1,
      );
    }

    return {
      paths,
      fallback: false,
    };
  };

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
  previewData,
}: GetStaticPropsContext) => {
  const slug = params?.slug as string;
  const locale = params?.locale as string;
  const previewDataPayload = previewData as ContentfulPreviewData;

  const pageTypes = CONTENT_TYPE_PRODUCT_PAGE;

  const pagesByTypes = await getEntriesByPageType(
    {
      'fields.slug': slug,
      locale,
    },
    pageTypes,
    preview,
    previewDataPayload,
  );

  const { header, footer, footerLegal } = await loadLayout(
    locale,
    preview,
    previewDataPayload,
  );

  const tariffDisplaySettings = (await getEntries(
    {
      contentType: 'displaySettings',
      'fields.entryName': 'Tariff Settings',
      'fields.id': 'tariff_settings',
      locale,
    },
    preview,
    previewDataPayload,
    false,
  )) as any;

  const v3Translations = await getEntriesByPageType(
    {
      'fields.contentGroup': 'subflow-v3',
      locale,
    },
    CONTENT_TYPE_PAGE,
    preview,
    previewDataPayload,
  );

  if (v3Translations?.[0].items?.length === 0) {
    return {
      notFound: true,
    };
  }

  let page = null;

  // Finds the first page type that has an item in it.
  for (const pagesByType of pagesByTypes) {
    if (pagesByType.items.length > 0) {
      page = pagesByType;

      break;
    }
  }

  if (!page || page.items?.length === 0) {
    return {
      notFound: true,
    };
  }

  if (page) {
    page = await fetchShopifyProducts(page);
  }

  const contentfulPage = page.items?.[0];

  const fieldName = shopifyProductField();

  let inventoryStatusCopy = null;

  let productVariantsCopy = null;

  let productPageGeneralCopy = null;

  if (
    [
      CONTENT_TYPE_PRODUCT_PAGE.PRODUCT_PAGE,
      CONTENT_TYPE_PRODUCT_PAGE.PLAY_KIT_PAGE,
    ].includes(getContentType(contentfulPage) as CONTENT_TYPE_PRODUCT_PAGE)
  ) {
    inventoryStatusCopy = await getInventoryStatusCopy(
      locale,
      preview,
      previewDataPayload,
    );

    productVariantsCopy = await getProductVariantsCopy(
      locale,
      preview,
      previewDataPayload,
    );

    productPageGeneralCopy = await getProductPageGeneralCopy(
      locale,
      preview,
      previewDataPayload,
    );
  }

  const shopifyAdditionalProducts = [] as ShopifyProduct[];

  let shopifyAlternativeProduct = null;

  let yotpoReviews: ReviewTotals | undefined = undefined;

  const contentfulPageFields = contentfulPage?.fields as TypeProductPageFields;

  if ('product' in contentfulPage?.fields) {
    const productFields = contentfulPageFields.product.fields;

    const productId = productFields[
      fieldName as keyof TypeProductFields
    ] as string;

    if (productId) {
      yotpoReviews = await getReviewTotals(
        contentfulPage?.fields.product as TypeProduct,
        locale,
      );
    }
  }

  if ('additionalProducts' in contentfulPageFields) {
    const additionalProducts = contentfulPageFields.additionalProducts;

    if (additionalProducts) {
      await Promise.all(
        additionalProducts.map(async (product) => {
          const productId = product.fields[
            fieldName as keyof TypeProductFields
          ] as string;

          if (productId) {
            const product = await getProductById(productId, locale);

            if (product) {
              shopifyAdditionalProducts.push(product);
            }
          }
        }),
      );
    }
  }

  const alternativeProductId = contentfulPageFields.alternativeProduct?.fields[
    fieldName as keyof TypeProductFields
  ] as string;

  if (alternativeProductId) {
    const shopifyProduct = await getProductById(alternativeProductId, locale);

    if (shopifyProduct) {
      shopifyAlternativeProduct = shopifyProduct;
    }
  }

  const subscriptionPlanAnchor =
    contentfulPageFields.product?.fields?.subscriptionPlanAnchor ||
    contentfulPageFields.alternativeProduct?.fields?.subscriptionPlanAnchor;

  const subscriptionPlanInstallmentsResponse = await getStartingInstallments({
    subscriptionPlanAnchor,
  });

  let theThinkerPlayKit = null;

  if (slug.includes('the-thinker')) {
    //  Calculates the date it was 360 days ago (so orchestration_subflow endpoint returns The Thinker PlayKit) | YYYY-MM-DD
    const aYearAgoDate = new Date(Date.now() - 360 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);

    const lang = getLanguageFromLocale(locale);
    // This is used to retrieve customizable options for The Thinker
    theThinkerPlayKit = await getStartingProduct({
      params: `date_of_birth=${aYearAgoDate}&child_name=&selected_language=${lang}&language=${lang}&currency_code=usd`,
    });
  }

  return {
    props: {
      shopifyAdditionalProducts,
      shopifyAlternativeProduct,
      page: contentfulPage || null,
      header,
      footer,
      footerLegal,
      inventoryStatusCopy,
      productVariantsCopy,
      productPageGeneralCopy,
      yotpoReviews: yotpoReviews || null,
      subscriptionPlanInstallments: subscriptionPlanInstallmentsResponse,
      theThinkerPlayKit,
      v3Translations: v3Translations?.[0].items?.[0] || null,
      tariffSettings: {
        translations:
          generateTranslationsObject(
            tariffDisplaySettings?.items?.[0]?.fields.translations?.fields,
          ) || null,
        exclusionList:
          tariffDisplaySettings?.items?.[0]?.fields.exclusionList?.fields
            .list || [],
      },
    },
    revalidate: defaultCacheRevalidation,
  };
};

export default ProductPage;
